import { persistAttribution, clearAttribution } from '@enovatedesign/astro-lead-forms/attribution';

// Consent Mode v2 state.
//
// Lives in an Alpine store rather than an x-data component because Layout.astro
// destroys and rebuilds the entire Alpine tree on every view transition — a
// component's init() re-runs per navigation, a store's does not. It also lets
// the footer's "Cookie settings" link reach the notice without a window global.
//
// The <head> script in GoogleTagManager.astro has already read this same record
// and set the consent *defaults* from it before any tag fired, so init() here
// deliberately does not re-push an update. The only pushes come from a user
// actually making or changing a choice.

const STORAGE_KEY = 'enovate.consent.v2';
const CONSENT_VERSION = 2;

// The pre-consent-rewrite key. Dismissing the old "Got it!" banner set it and
// was treated as consent to advertising cookies, which it never was — so it is
// not migrated. Those visitors are re-prompted, and the stale key is removed.
const LEGACY_KEY = 'enovate.cookieNoticeDismissed';

export const readConsent = () => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        const parsed = raw ? JSON.parse(raw) : null;
        return (parsed && parsed.v === CONSENT_VERSION) ? parsed : null;
    } catch (e) {
        return null;
    }
};

/**
 * For code outside Alpine that needs to know whether it may hand personal data
 * to an advertising tag; passed to registerLeadForms() in src/entrypoint.js.
 */
export const hasMarketingConsent = () => Boolean(readConsent()?.marketing);

// Records what was consented to, when, and against which version of the notice.
// Storing a bare "dismissed" flag — as the old implementation did — can't
// demonstrate consent, which Article 7(1) requires.
const persist = (analytics, marketing) => {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({
            v: CONSENT_VERSION,
            analytics,
            marketing,
            ts: new Date().toISOString(),
        }));
    } catch (e) {
        // Storage unavailable — the decision still applies for this page load.
    }
};

const apply = (analytics, marketing) => {
    try {
        window.gtag?.('consent', 'update', {
            analytics_storage: analytics ? 'granted' : 'denied',
            ad_storage: marketing ? 'granted' : 'denied',
            ad_user_data: marketing ? 'granted' : 'denied',
            ad_personalization: marketing ? 'granted' : 'denied',
            personalization_storage: marketing ? 'granted' : 'denied',
        });
        // Lets GTM triggers react to a consent change without polling. Resolve
        // dataLayer per call for the same reason the head script's gtag shim
        // does: something may replace the array briefly before
        // re-injecting GTM on the main thread, and a consent change landing in
        // that window would otherwise be dropped silently.
        (window.dataLayer = window.dataLayer || []).push({
            event: 'consent_update',
            consent_analytics: analytics ? 'granted' : 'denied',
            consent_marketing: marketing ? 'granted' : 'denied',
        });
    } catch (e) {
        // A consent-signalling failure must never
        // let a consent-tracking failure surface as an Alpine expression error.
    }

    if (marketing) persistAttribution();
    else clearAttribution();
};

const consentStore = {
    analytics: false,
    marketing: false,

    // A decision has been recorded. Distinct from "the notice is hidden", which
    // is also true while the visitor is mid-way through managing preferences.
    decided: false,

    showNotice: false,
    showDetail: false,

    init() {
        const stored = readConsent();

        try {
            localStorage.removeItem(LEGACY_KEY);
        } catch (e) {
            // Nothing to clean up if storage is unavailable.
        }

        if (stored) {
            this.analytics = Boolean(stored.analytics);
            this.marketing = Boolean(stored.marketing);
            this.decided = true;
            // Consent defaults were already set from this record in the head, so
            // there is nothing to re-apply to gtag. Attribution does still need
            // telling, since that lives in module state that starts each load
            // assuming no consent.
            if (this.marketing) persistAttribution();
        } else {
            this.showNotice = true;
        }
    },

    // Reopens the notice with preferences expanded. This is what satisfies the
    // Article 7(3) requirement that withdrawing consent be as easy as giving it,
    // so the control that calls it has to be reachable from every page.
    open() {
        const stored = readConsent();
        if (stored) {
            this.analytics = Boolean(stored.analytics);
            this.marketing = Boolean(stored.marketing);
        }
        this.showDetail = true;
        this.showNotice = true;
        window.dispatchEvent(new CustomEvent('enovate:consent-open'));
    },

    acceptAll() {
        this.analytics = true;
        this.marketing = true;
        this.commit();
    },

    rejectAll() {
        this.analytics = false;
        this.marketing = false;
        this.commit();
    },

    savePreferences() {
        this.commit();
    },

    commit() {
        persist(this.analytics, this.marketing);
        apply(this.analytics, this.marketing);
        this.decided = true;
        this.showNotice = false;

        // Deliberately does NOT reset showDetail. Collapsing the preferences
        // panel in the same tick the notice hides makes Alpine coordinate two
        // nested leave transitions: x-show on the notice, x-collapse on the
        // panel. The child registers a hide promise against the hiding parent,
        // and the parent then never completes its own hide — the notice stays
        // on screen with the state already flipped to hidden. Leaving the panel
        // expanded costs nothing, because it's inside an element that is on its
        // way out, and open() sets showDetail explicitly every time.
    },
};

export const registerConsentStore = (Alpine) => {
    Alpine.store('consent', consentStore);
};
